var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0, _descriptor1;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { action, observable, runInAction } from 'mobx';
import { featureFlagsClient } from '@winnin/feature-flags';
import PROVIDERS, { providersWithTwitch, PUBLIC_PROVIDERS_OPTIONS } from 'consts/providers';
import datadog from 'utils/datadog';
import getCurrentWorkspace from 'utils/getCurrentWorkspace';
import getEnvFromLocation from 'utils/getEnvFromLocation';
import { FEATURE_FLAGS_LOCAL_STORAGE_KEY } from './feature-flags.consts';
import type { FeatureFlag, TextSetting, UserInfo } from './feature-flags.types';
const TOKEN = {
  production: import.meta.env.REACT_APP_CONFIGCAT_SDK_KEY_PRODUCTION,
  development: import.meta.env.REACT_APP_CONFIGCAT_SDK_KEY_DEVELOPMENT
};
let FeatureFlagsStore = (_class = class FeatureFlagsStore {
  constructor() {
    _initializerDefineProperty(this, "featureFlagsValues", _descriptor, this);
    _initializerDefineProperty(this, "remoteFeatureFlagsValues", _descriptor2, this);
    _initializerDefineProperty(this, "isFeatureFlagsValuesReady", _descriptor3, this);
    _initializerDefineProperty(this, "isLoggerEnabled", _descriptor4, this);
    _initializerDefineProperty(this, "userInfo", _descriptor5, this);
    _initializerDefineProperty(this, "providersOptions", _descriptor6, this);
    this.storageKey = FEATURE_FLAGS_LOCAL_STORAGE_KEY;
    this.workspaceId = undefined;
    _initializerDefineProperty(this, "init", _descriptor7, this);
    _initializerDefineProperty(this, "setUserAndGetFlags", _descriptor8, this);
    _initializerDefineProperty(this, "refreshFlags", _descriptor9, this);
    /** Optimistically updates a single flag value in the observable so all @observer
     * components re-render immediately, before the async ConfigCat fetch resolves. */
    _initializerDefineProperty(this, "setFlagOverride", _descriptor0, this);
    /** Optimistically restores flag values from the remote snapshot so components
     * re-render immediately when all overrides are cleared. */
    _initializerDefineProperty(this, "clearFlagOverrides", _descriptor1, this);
  }
  private getWorkspaceId() {
    if (!this.workspaceId) {
      this.workspaceId = getCurrentWorkspace();
    }
    return this.workspaceId;
  }
  setFeatureFlagProviders() {
    const source = this.featureFlagsValues['istiktokenabled'] ? PROVIDERS : providersWithTwitch;
    this.providersOptions = source.NAMES.map(v => ({
      value: v.KEY,
      text: source[v.KEY].NAME,
      label: source[v.KEY].NAME,
      color: source[v.KEY].COLOR
    }));
  }

  // Reads from the MobX @observable — triggers re-renders in @observer components.
  // Also calls featureFlagsClient.isEnabled() as a side-effect so the onEvaluate hook
  // (Datadog RUM feature-flag tracking) still fires on every boolean flag read.
  isFeatureFlagEnabled(flag: FeatureFlag) {
    featureFlagsClient.isEnabled(flag);
    return !!this.featureFlagsValues[flag];
  }
  getFeatureFlagValueEvaluation(flag: TextSetting) {
    return featureFlagsClient.getValue(flag);
  }

  // Alias of isFeatureFlagEnabled for semantic clarity in @observer components
  getFeatureFlag(flag: FeatureFlag) {
    return this.isFeatureFlagEnabled(flag);
  }
  async toggleFeatureFlagLogEvents() {
    featureFlagsClient.toggleLogEvents();
    this.isLoggerEnabled = featureFlagsClient.isLogEnabled();
  }
  private envToken() {
    const envUrl = getEnvFromLocation();
    switch (envUrl) {
      case 'production':
      case 'staging':
        return TOKEN.production;
      case 'local':
      case 'test':
      case 'development':
        return TOKEN.development;
      default:
        throw new Error(`@FeatureFlagsStore >>> Invalid env name for configCat. ${envUrl}`);
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "featureFlagsValues", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "remoteFeatureFlagsValues", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "isFeatureFlagsValuesReady", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "isLoggerEnabled", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "userInfo", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "providersOptions", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return PUBLIC_PROVIDERS_OPTIONS;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "init", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return async () => {
      featureFlagsClient.subscribe(action(flags => {
        this.featureFlagsValues = flags;
        this.remoteFeatureFlagsValues = featureFlagsClient.getRemoteSnapshot();
        this.setFeatureFlagProviders();
      }));
      try {
        await featureFlagsClient.init({
          token: this.envToken(),
          getUser: () => {
            const {
              id,
              email,
              company,
              releaseType
            } = this.userInfo;
            return {
              identifier: id,
              email,
              custom: {
                companyName: company?.name,
                companyId: company?.id,
                workspaceId: this.getWorkspaceId(),
                releaseType
              }
            };
          },
          onEvaluate: (flag, value) => datadog.addFeatureFlagEvaluation(flag, value),
          localOverridesStorageKey: this.storageKey
        });
      } finally {
        runInAction(() => {
          this.isFeatureFlagsValuesReady = true;
        });
      }
    };
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "setUserAndGetFlags", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return async (user: UserInfo) => {
      this.userInfo = user;
      await this.refreshFlags();
    };
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "refreshFlags", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return async () => {
      await featureFlagsClient.refreshFlags();
    };
  }
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "setFlagOverride", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return (flag: FeatureFlag, value: boolean) => {
      this.featureFlagsValues[flag] = value;
    };
  }
}), _descriptor1 = _applyDecoratedDescriptor(_class.prototype, "clearFlagOverrides", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return () => {
      this.featureFlagsValues = {
        ...this.remoteFeatureFlagsValues
      };
    };
  }
}), _applyDecoratedDescriptor(_class.prototype, "setFeatureFlagProviders", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setFeatureFlagProviders"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggleFeatureFlagLogEvents", [action], Object.getOwnPropertyDescriptor(_class.prototype, "toggleFeatureFlagLogEvents"), _class.prototype), _class);
export default new FeatureFlagsStore();